import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const direction = current >= this.#current ? 1 : -1;
    const previousIndex = this.#relativeIndex(this.#current);
    this.#current = current;

    const relativeIndex = this.#relativeIndex(current);

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });

    if (this.dataset.transition === 'slide' && previousIndex !== relativeIndex) {
      this.#slide(previousIndex, relativeIndex, direction);
    }
  }

  /**
   * @param {number} index
   * @returns {number}
   */
  #relativeIndex(index) {
    const count = (this.refs.slides ?? []).length || 1;
    return ((index % count) + count) % count;
  }

  /**
   * Slides the outgoing announcement out and the incoming one in, sideways.
   * Moving forward goes right to left; going back reverses it.
   * @param {number} fromIndex
   * @param {number} toIndex
   * @param {number} direction - 1 for next, -1 for previous
   */
  #slide(fromIndex, toIndex, direction) {
    const outgoing = this.refs.slides?.[fromIndex];
    const incoming = this.refs.slides?.[toIndex];
    if (!outgoing || !incoming || !outgoing.animate) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const options = { duration: 600, easing: 'cubic-bezier(0.65, 0, 0.35, 1)' };
    outgoing.animate(
      [
        { transform: 'translateX(0)', opacity: 1, visibility: 'visible' },
        { transform: `translateX(${-direction * 100}%)`, opacity: 0, visibility: 'visible' },
      ],
      options
    );
    incoming.animate(
      [
        { transform: `translateX(${direction * 100}%)`, opacity: 0 },
        { transform: 'translateX(0)', opacity: 1 },
      ],
      options
    );
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
